import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import Logo from "./Logo";
import { useLang, NAV_T } from "../../lib/lang";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";

const TRANSFER_LINKS = [
  ["Airport Transfers", "/airport-transfers"],
  ["Port Transfers", "/transfers/port"],
  ["Private Transfers", "/transfers/private"],
  ["Hourly Chauffeur", "/chauffeur"],
];

const linkCls = ({ isActive }) =>
  `text-[15px] transition-colors duration-200 hover:text-[#183C35] ${isActive ? "text-[#183C35] font-medium" : "text-[#202522]/75"}`;

const Header = () => {
  const { lang, setLang } = useLang();
  const t = NAV_T[lang];
  const [open, setOpen] = useState(false);
  const [ddOpen, setDdOpen] = useState(false);
  
  return (
    <header className="sticky top-0 z-50 bg-[#F6F2E9]/90 backdrop-blur-md border-b border-[#183C35]/10" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 h-[72px] flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-5 xl:gap-7" aria-label="Main navigation">
          <NavLink to="/" end className={linkCls} data-testid="nav-home">{t.home}</NavLink>
          <div onMouseEnter={() => setDdOpen(true)} onMouseLeave={() => setDdOpen(false)}>
            <DropdownMenu open={ddOpen} onOpenChange={setDdOpen} modal={false}>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1 text-[15px] text-[#202522]/75 hover:text-[#183C35] outline-none transition-colors" data-testid="nav-transfers">
                  {t.transfers} <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${ddOpen ? "rotate-180" : ""}`} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" sideOffset={10} className="w-60 rounded-xl p-2 border-[#183C35]/10 shadow-xl bg-white">
                {TRANSFER_LINKS.map(([l, h]) => (
                  <DropdownMenuItem key={h} asChild className="px-3 py-2.5 rounded-lg text-[15px] text-[#202522] cursor-pointer focus:bg-[#F6F2E9] focus:text-[#183C35]" data-testid={`nav-dd-${h.split("/").pop()}`}>
                    <Link to={h}>{l}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <NavLink to="/tours" className={linkCls} data-testid="nav-tours">{t.tours}</NavLink>
          <NavLink to="/fleet" className={linkCls} data-testid="nav-fleet">{t.fleet}</NavLink>
          <NavLink to="/about" className={linkCls} data-testid="nav-about">{t.about}</NavLink>
          <NavLink to="/corfu-guide" className={linkCls} data-testid="nav-guide">{t.guide}</NavLink>
          <NavLink to="/contact" className={linkCls} data-testid="nav-contact">{t.contact}</NavLink>
        </nav>
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center rounded-full border border-[#183C35]/15 p-[3px] bg-white/50">
            {["EN", "GR"].map((l) => (
              <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}`}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors duration-200 ${lang === l ? "bg-[#183C35] text-[#F7F3EA]" : "text-[#183C35]/70 hover:text-[#183C35]"}`}>
                {l}
              </button>
            ))}
          </div>
          <Link to="/book" data-testid="header-book-now"
            className="hidden sm:inline-flex rounded-full bg-[#183C35] text-[#F7F3EA] px-5 py-2.5 text-[15px] font-medium hover:bg-[#21524A] transition-colors duration-200 shadow-sm">
            {t.book}
          </Link>
          <button className="lg:hidden p-2 text-[#183C35]" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      <nav className="sr-only" aria-label="Site links">
        {[["Aloha Corfu Transfers", "/"], ["Airport Transfers", "/airport-transfers"], ["Port Transfers", "/transfers/port"], ["Private Tours in Corfu", "/tours"], ["Chauffeur Service", "/chauffeur"], ["Fleet", "/fleet"], ["Book a Transfer", "/book"], ["About Us", "/about"], ["Contact", "/contact"]].map(([l, h]) => <a key={h} href={h}>{l}</a>)}
      </nav>
      {open && (
        <div className="lg:hidden border-t border-[#183C35]/10 bg-[#F6F2E9] px-6 py-4 space-y-1 fade-up" data-testid="mobile-menu">
          {[[t.home, "/"], ...TRANSFER_LINKS, [t.tours, "/tours"], [t.fleet, "/fleet"], [t.about, "/about"], [t.guide, "/corfu-guide"], [t.contact, "/contact"]].map(([l, h]) => (
            <Link key={h} to={h} onClick={() => setOpen(false)} className="block py-2.5 text-[15px] text-[#202522] border-b border-[#183C35]/5">{l}</Link>
          ))}
          <div className="flex items-center justify-between pt-3">
            <div className="flex rounded-full border border-[#183C35]/15 p-[3px]">
              {["EN", "GR"].map((l) => (
                <button key={l} onClick={() => setLang(l)} className={`px-3 py-1 rounded-full text-xs font-semibold ${lang === l ? "bg-[#183C35] text-[#F7F3EA]" : "text-[#183C35]"}`}>{l}</button>
              ))}
            </div>
            <Link to="/book" onClick={() => setOpen(false)} className="rounded-full bg-[#183C35] text-[#F7F3EA] px-5 py-2.5 text-sm font-medium">{t.book}</Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
